import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Monitor, Users, DollarSign, Clock, CheckCircle, XCircle, AlertTriangle,
    Play, Loader2, TrendingUp, Banknote, Eye, RefreshCw, Calculator,
    Lock, Unlock, ArrowRightLeft, History, UserCheck, AlertCircle,
    X, Check, Settings, ChevronRight, LogIn, ExternalLink, CreditCard, Coins,
    AlertOctagon, ShieldAlert
} from 'lucide-react';
import { cn } from '../lib/utils';
import MobilePageHeader from '../components/MobilePageHeader';
import CashClosingModal from '../components/CashClosingModal';

const RegisterManagementPage = () => {
    const navigate = useNavigate();
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [dashboard, setDashboard] = useState([]);
    const [connectedUsers, setConnectedUsers] = useState([]);
    const [availableRegisters, setAvailableRegisters] = useState([]);
    const [pendingValidations, setPendingValidations] = useState([]);
    const [sessionHistory, setSessionHistory] = useState([]);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [settings, setSettings] = useState({});
    
    // Dialogs
    const [openDialog, setOpenDialog] = useState({ open: false });
    const [transferDialog, setTransferDialog] = useState({ open: false, session: null });
    const [closeDialog, setCloseDialog] = useState({ open: false, session: null });
    const [unlockDialog, setUnlockDialog] = useState({ open: false, session: null });
    const [validationDialog, setValidationDialog] = useState({ open: false, session: null });
    const [historyDialog, setHistoryDialog] = useState({ open: false, session: null });
    const [settingsDialog, setSettingsDialog] = useState(false);
    
    // Cash Closing Modal (detailed counting)
    const [cashClosingModal, setCashClosingModal] = useState({ open: false, registerId: null, registerName: '' });
    
    // Emergency Close Dialog (Admin only)
    const [emergencyDialog, setEmergencyDialog] = useState({ open: false, session: null });
    const [emergencyPassword, setEmergencyPassword] = useState('');
    const [emergencyNotes, setEmergencyNotes] = useState('');
    
    // Form states
    const [selectedRegister, setSelectedRegister] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [openingCash, setOpeningCash] = useState('');
    const [countedCash, setCountedCash] = useState('');
    const [closingNotes, setClosingNotes] = useState('');
    const [transferUser, setTransferUser] = useState('');
    const [transferNotes, setTransferNotes] = useState('');
    const [unlockPin, setUnlockPin] = useState('');
    const [validationNotes, setValidationNotes] = useState('');
    const [sessionEvents, setSessionEvents] = useState([]);
    const [processing, setProcessing] = useState(false);
    
    const isAdmin = user?.role === 'ADMIN';
    const isManager = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [dashboardRes, pendingRes, historyRes, settingsRes] = await Promise.all([
                api().get('/registers/dashboard'),
                isManager ? api().get('/registers/sessions/pending-validation') : Promise.resolve({ data: [] }),
                api().get('/registers/sessions/history?limit=20'),
                api().get('/registers/settings')
            ]);
            
            setDashboard(dashboardRes.data || []);
            setPendingValidations(pendingRes.data || []);
            setSessionHistory(historyRes.data || []);
            setSettings(settingsRes.data || {});
        } catch (error) {
            console.error('Error fetching data:', error);
            if (!error.response?.status === 404) {
                toast.error('Erreur de chargement des données');
            }
        } finally {
            setLoading(false);
        }
    }, [api, isManager]);

    const fetchOpenDialogData = async () => {
        try {
            const [usersRes, registersRes] = await Promise.all([
                api().get('/registers/connected-users'),
                api().get('/registers/available')
            ]);
            setConnectedUsers(usersRes.data || []);
            setAvailableRegisters(registersRes.data || []);
        } catch (error) {
            console.error('Error fetching dialog data:', error);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const handleOpenRegister = async () => {
        if (!selectedRegister || !selectedUser) {
            toast.error('Veuillez sélectionner une caisse et un utilisateur');
            return;
        }
        
        const register = availableRegisters.find(r => r.id === selectedRegister);
        if (!register?.is_available) {
            toast.error('Cette caisse n\'est plus disponible');
            return;
        }
        
        try {
            setProcessing(true);
            await api().post('/registers/open', {
                register_id: selectedRegister,
                assigned_user_id: selectedUser,
                opening_cash: parseFloat(openingCash) || 0
            });
            
            const selectedUserName = connectedUsers.find(u => u.id === selectedUser)?.name || '';
            const registerName = register.name || '';
            toast.success(`Caisse "${registerName}" ouverte pour ${selectedUserName}`);
            
            setOpenDialog({ open: false });
            setSelectedRegister('');
            setSelectedUser('');
            setOpeningCash('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ouverture');
        } finally {
            setProcessing(false);
        }
    };

    const handleTransfer = async () => {
        if (!transferUser || !transferDialog.session) {
            toast.error('Veuillez sélectionner un utilisateur');
            return;
        }
        
        try {
            setProcessing(true);
            await api().post(`/registers/${transferDialog.session.session_id}/transfer`, {
                new_user_id: transferUser,
                notes: transferNotes || null
            });
            
            toast.success('Caisse transférée avec succès');
            setTransferDialog({ open: false, session: null });
            setTransferUser('');
            setTransferNotes('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du transfert');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = async () => {
        if (!countedCash || !closeDialog.session) {
            toast.error('Veuillez saisir le montant compté');
            return;
        }
        
        try {
            setProcessing(true);
            await api().post(`/registers/${closeDialog.session.session_id}/close`, {
                counted_cash: parseFloat(countedCash),
                notes: closingNotes || null
            });
            
            toast.success('Caisse fermée avec succès');
            setCloseDialog({ open: false, session: null });
            setCountedCash('');
            setClosingNotes('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la fermeture');
        } finally {
            setProcessing(false);
        }
    };

    const handleUnlock = async () => {
        // ADMIN can unlock without PIN - full access
        if (!isAdmin && (!unlockPin || !unlockDialog.session)) {
            toast.error('Veuillez saisir votre PIN');
            return;
        }
        
        try {
            setProcessing(true);
            await api().post(`/registers/${unlockDialog.session.session_id}/unlock`, {
                pin: isAdmin ? '0000' : unlockPin  // ADMIN sends dummy PIN, backend will bypass
            });
            
            toast.success('Caisse déverrouillée');
            setUnlockDialog({ open: false, session: null });
            setUnlockPin('');
            
            // Redirect to POS after successful unlock
            navigate('/pos');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'PIN incorrect');
        } finally {
            setProcessing(false);
        }
    };

    const handleValidation = async (approved) => {
        if (!validationDialog.session) return;
        
        try {
            setProcessing(true);
            await api().post(`/registers/${validationDialog.session.id}/validate`, {
                approved,
                manager_notes: validationNotes || null
            });
            
            toast.success(approved ? 'Clôture validée' : 'Clôture rejetée - caisse réouverte');
            setValidationDialog({ open: false, session: null });
            setValidationNotes('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setProcessing(false);
        }
    };

    const fetchSessionEvents = async (sessionId) => {
        try {
            const res = await api().get(`/registers/sessions/${sessionId}/events`);
            setSessionEvents(res.data || []);
        } catch (error) {
            console.error('Error fetching events:', error);
        }
    };

    const handleSaveSettings = async () => {
        try {
            setProcessing(true);
            await api().put('/registers/settings', settings);
            toast.success('Paramètres enregistrés');
            setSettingsDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setProcessing(false);
        }
    };

    // Emergency Close Handler (Admin only)
    const handleEmergencyClose = async () => {
        if (!emergencyPassword || !emergencyDialog.session) {
            toast.error('Veuillez saisir votre mot de passe');
            return;
        }
        
        try {
            setProcessing(true);
            await api().post(`/registers/${emergencyDialog.session.session_id}/emergency-close`, {
                admin_password: emergencyPassword,
                notes: emergencyNotes || null
            });
            
            toast.success('Fermeture d\'urgence effectuée - Caisse disponible');
            setEmergencyDialog({ open: false, session: null });
            setEmergencyPassword('');
            setEmergencyNotes('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la fermeture d\'urgence');
        } finally {
            setProcessing(false);
        }
    };

    const formatCurrency = (amount) => {
        return (amount || 0).toLocaleString('fr-FR') + ' F';
    };

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleTimeString('fr-FR', {
            timeZone: 'Africa/Libreville',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleDateString('fr-FR', {
            timeZone: 'Africa/Libreville',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getStatusBadge = (item) => {
        if (item.status === 'closed' || item.session_status === null) {
            return <Badge variant="outline" className="text-gray-500">Fermée</Badge>;
        }
        if (item.session_status === 'locked' || item.is_locked) {
            return <Badge className="bg-amber-500"><Lock className="w-3 h-3 mr-1" />Verrouillée</Badge>;
        }
        if (item.session_status === 'pending_validation') {
            return <Badge className="bg-orange-500"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
        }
        return <Badge className="bg-green-500">Ouverte</Badge>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Chargement...</p>
                </div>
            </div>
        );
    }

    const openCount = dashboard.filter(d => d.status === 'open').length;
    const lockedCount = dashboard.filter(d => d.is_locked).length;
    const totalSales = dashboard.reduce((sum, d) => sum + (d.total_sales || 0), 0);

    return (
        <div className="container mx-auto py-4 px-3 sm:px-4 max-w-7xl" data-testid="register-management-page">
            {/* Mobile Page Header */}
            <MobilePageHeader 
                title="Gestion des Caisses"
                subtitle={`${openCount} caisses ouvertes`}
                icon={CreditCard}
                backPath="/dashboard"
            />
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                        <Monitor className="w-6 h-6 text-primary" />
                        Gestion des Caisses
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Ouvrez, transférez et supervisez les caisses
                    </p>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={fetchData}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                    {isAdmin && (
                        <Button variant="outline" size="sm" onClick={() => setSettingsDialog(true)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Paramètres
                        </Button>
                    )}
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2">
                            <Monitor className="w-5 h-5 text-blue-600" />
                            <div>
                                <p className="text-2xl font-bold text-blue-700">{openCount}</p>
                                <p className="text-xs text-blue-600">Caisses ouvertes</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2">
                            <Lock className="w-5 h-5 text-amber-600" />
                            <div>
                                <p className="text-2xl font-bold text-amber-700">{lockedCount}</p>
                                <p className="text-xs text-amber-600">Verrouillées</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-orange-600" />
                            <div>
                                <p className="text-2xl font-bold text-orange-700">{pendingValidations.length}</p>
                                <p className="text-xs text-orange-600">En attente</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                            <div>
                                <p className="text-2xl font-bold text-green-700">{formatCurrency(totalSales)}</p>
                                <p className="text-xs text-green-600">Ventes totales</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pending Validations Alert */}
            {pendingValidations.length > 0 && isManager && (
                <Card className="mb-6 border-orange-300 bg-orange-50">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-orange-800">
                            <AlertTriangle className="w-5 h-5" />
                            {pendingValidations.length} clôture(s) avec écart à valider
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {pendingValidations.map((session) => (
                                <div 
                                    key={session.id}
                                    className="flex items-center justify-between p-3 bg-white rounded-lg border"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                            <Calculator className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{session.assigned_user_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {session.register_name} - {formatDate(session.date)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <p className={cn(
                                                "text-lg font-bold",
                                                session.difference >= 0 ? "text-green-600" : "text-red-600"
                                            )}>
                                                {session.difference >= 0 ? '+' : ''}{formatCurrency(session.difference)}
                                            </p>
                                            <p className="text-xs text-muted-foreground">Écart</p>
                                        </div>
                                        <Button 
                                            size="sm"
                                            onClick={() => setValidationDialog({ open: true, session })}
                                        >
                                            Valider
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                    <TabsTrigger value="dashboard" className="gap-2">
                        <Monitor className="w-4 h-4" />
                        Tableau de bord
                    </TabsTrigger>
                    <TabsTrigger value="history" className="gap-2">
                        <History className="w-4 h-4" />
                        Historique
                    </TabsTrigger>
                </TabsList>

                {/* Dashboard Tab */}
                <TabsContent value="dashboard">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        {dashboard.map((item) => {
                            // Determine if current user is assigned to this register
                            const isMyRegister = item.assigned_user_id === user?.id;
                            const isOpen = item.status === 'open';
                            const isClosed = item.status === 'closed' || !item.session_id;
                            const isEmergencyClosed = item.closure_type === 'emergency';
                            
                            // Check if this is an Admin-only register
                            const registerName = (item.register_name || '').toLowerCase();
                            const registerSector = (item.sector || '').toLowerCase();
                            const isAdminOnlyRegister = registerName.includes('admin') || registerSector === 'admin';
                            const canAccessAdminRegister = isAdmin || isMyRegister;
                            
                            return (
                            <Card 
                                key={item.register_id}
                                className={cn(
                                    "overflow-hidden transition-all",
                                    isOpen && isMyRegister && "ring-1 ring-green-500 shadow-sm shadow-green-500/20",
                                    isOpen && !isMyRegister && "ring-1 ring-blue-400",
                                    item.is_locked && "ring-1 ring-amber-500"
                                )}
                            >
                                <div className={cn(
                                    "h-1.5",
                                    isClosed && "bg-gray-200",
                                    isOpen && !item.is_locked && isMyRegister && "bg-green-500",
                                    isOpen && !item.is_locked && !isMyRegister && "bg-blue-500",
                                    item.is_locked && "bg-amber-500"
                                )} />
                                <CardHeader className="p-2 pb-1">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-lg">{item.icon || '💰'}</span>
                                            <CardTitle className="text-sm font-semibold">{item.register_name}</CardTitle>
                                        </div>
                                        {getStatusBadge(item)}
                                    </div>
                                    <CardDescription className="capitalize text-xs">{item.sector}</CardDescription>
                                </CardHeader>
                                <CardContent className="p-2 pt-0 space-y-1.5">
                                    {isOpen ? (
                                        <>
                                            {/* User Info - Compact */}
                                            <div className="p-1.5 bg-muted rounded text-xs space-y-0.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-muted-foreground">Assignée</span>
                                                    <span className={cn(
                                                        "font-medium truncate max-w-[80px]",
                                                        isMyRegister && "text-green-600"
                                                    )}>
                                                        {isMyRegister ? "Vous" : item.assigned_user_name?.split(' ')[0]}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-muted-foreground">Ouverture</span>
                                                    <span className="font-medium">{formatTime(item.opened_at)}</span>
                                                </div>
                                            </div>
                                            
                                            {/* Financial Info - Compact */}
                                            <div className="text-xs space-y-0.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-muted-foreground">Fond</span>
                                                    <span className="font-medium">{formatCurrency(item.opening_cash)}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-muted-foreground">Ventes</span>
                                                    <span className="font-bold text-green-600">{formatCurrency(item.total_sales)}</span>
                                                </div>
                                                <div className="flex items-center justify-between border-t pt-0.5">
                                                    <span className="font-medium">Attendu</span>
                                                    <span className="font-bold">{formatCurrency(item.expected_cash)}</span>
                                                </div>
                                            </div>
                                            
                                            {/* MAIN ACTION BUTTON - Compact */}
                                            <div className="pt-1 border-t">
                                                {item.is_locked ? (
                                                    <Button 
                                                        className="w-full h-8 bg-amber-600 hover:bg-amber-700 text-xs font-bold"
                                                        onClick={() => setUnlockDialog({ open: true, session: item })}
                                                        data-testid={`unlock-register-${item.register_id}`}
                                                    >
                                                        <Unlock className="w-3.5 h-3.5 mr-1" />
                                                        Déverrouiller
                                                    </Button>
                                                ) : isMyRegister ? (
                                                    <Button 
                                                        className="w-full h-8 bg-green-600 hover:bg-green-700 text-xs font-bold"
                                                        onClick={() => navigate('/pos')}
                                                        data-testid={`access-register-${item.register_id}`}
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5 mr-1" />
                                                        Accéder
                                                    </Button>
                                                ) : isAdminOnlyRegister && !isAdmin ? (
                                                    /* Admin-only register - show disabled button for non-admins */
                                                    <Button 
                                                        className="w-full h-8 text-xs"
                                                        variant="outline"
                                                        disabled
                                                    >
                                                        <Lock className="w-3.5 h-3.5 mr-1" />
                                                        Réservée Admin
                                                    </Button>
                                                ) : (
                                                    <Button 
                                                        className="w-full h-8 bg-blue-600 hover:bg-blue-700 text-xs font-bold"
                                                        onClick={() => {
                                                            setTransferDialog({ open: true, session: item });
                                                            fetchOpenDialogData();
                                                        }}
                                                        data-testid={`take-register-${item.register_id}`}
                                                    >
                                                        <LogIn className="w-3.5 h-3.5 mr-1" />
                                                        Prendre
                                                    </Button>
                                                )}
                                            </div>
                                            
                                            {/* Lock & Close Actions - Compact */}
                                            {/* Clôturer: Only for the register owner (or admin via emergency) */}
                                            {isMyRegister && !item.is_locked && (
                                                <div className="grid grid-cols-2 gap-1">
                                                    <Button 
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                                                        onClick={async () => {
                                                            try {
                                                                await api().post(`/registers/${item.session_id}/lock`);
                                                                toast.success('Caisse verrouillée');
                                                                fetchData();
                                                            } catch (error) {
                                                                toast.error(error.response?.data?.detail || 'Erreur');
                                                            }
                                                        }}
                                                    >
                                                        <Lock className="w-3 h-3 mr-0.5" />
                                                        Verr.
                                                    </Button>
                                                    <Button 
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                                        onClick={() => setCashClosingModal({ 
                                                            open: true, 
                                                            registerId: item.register_id, 
                                                            registerName: item.register_name,
                                                            sessionId: item.session_id
                                                        })}
                                                    >
                                                        <Coins className="w-3 h-3 mr-0.5" />
                                                        Clôt.
                                                    </Button>
                                                </div>
                                            )}
                                            
                                            {/* Lock only for managers (not owner) - no close button */}
                                            {!isMyRegister && isManager && !item.is_locked && (
                                                <Button 
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full h-7 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                                                    onClick={async () => {
                                                        try {
                                                            await api().post(`/registers/${item.session_id}/lock`);
                                                            toast.success('Caisse verrouillée');
                                                            fetchData();
                                                        } catch (error) {
                                                            toast.error(error.response?.data?.detail || 'Erreur');
                                                        }
                                                    }}
                                                >
                                                    <Lock className="w-3 h-3 mr-0.5" />
                                                    Verrouiller
                                                </Button>
                                            )}
                                            
                                            {/* Emergency Close - Admin Only */}
                                            {isAdmin && !item.is_locked && (
                                                <Button 
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full h-7 text-xs text-red-700 border-red-300 bg-red-50 hover:bg-red-100"
                                                    onClick={() => setEmergencyDialog({ open: true, session: item })}
                                                    data-testid={`emergency-close-${item.register_id}`}
                                                >
                                                    <AlertOctagon className="w-3 h-3 mr-1" />
                                                    Fermeture d'urgence
                                                </Button>
                                            )}
                                            
                                            {/* Mini Actions Row */}
                                            <div className="flex gap-1 pt-0.5">
                                                {isManager && !item.is_locked && !isMyRegister && (
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost"
                                                        className="h-6 px-2 text-xs"
                                                        onClick={() => {
                                                            setTransferDialog({ open: true, session: item });
                                                            fetchOpenDialogData();
                                                        }}
                                                    >
                                                        <ArrowRightLeft className="w-3 h-3" />
                                                    </Button>
                                                )}
                                                <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={() => {
                                                        setHistoryDialog({ open: true, session: item });
                                                        fetchSessionEvents(item.session_id);
                                                    }}
                                                >
                                                    <History className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        /* CLOSED REGISTER - Compact */
                                        <div className="py-2">
                                            <div className="text-center text-muted-foreground mb-2">
                                                <Monitor className="w-8 h-8 mx-auto mb-1 opacity-30" />
                                                <p className="text-xs">
                                                    {isAdminOnlyRegister ? 'Caisse Admin' : 'Caisse fermée'}
                                                </p>
                                            </div>
                                            
                                            {/* Admin-only registers can only be opened by Admin */}
                                            {isAdminOnlyRegister && !isAdmin ? (
                                                <Button 
                                                    className="w-full h-8 text-xs"
                                                    variant="outline"
                                                    disabled
                                                >
                                                    <Lock className="w-3.5 h-3.5 mr-1" />
                                                    Réservée Admin
                                                </Button>
                                            ) : isManager ? (
                                                <Button 
                                                    className="w-full h-8 bg-green-600 hover:bg-green-700 text-xs font-bold"
                                                    onClick={() => { 
                                                        setOpenDialog({ open: true }); 
                                                        setSelectedRegister(item.register_id);
                                                        fetchOpenDialogData(); 
                                                    }}
                                                    data-testid={`open-register-${item.register_id}`}
                                                >
                                                    <Play className="w-3.5 h-3.5 mr-1" />
                                                    Ouvrir
                                                </Button>
                                            ) : (
                                                <Button 
                                                    className="w-full h-8 text-xs"
                                                    variant="outline"
                                                    disabled
                                                >
                                                    <Lock className="w-3.5 h-3.5 mr-1" />
                                                    En attente
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                            );
                        })}
                    </div>
                </TabsContent>

                {/* History Tab */}
                <TabsContent value="history">
                    <Card>
                        <CardContent className="p-0">
                            <ScrollArea className="h-[500px]">
                                <div className="divide-y">
                                    {sessionHistory.map((session) => (
                                        <div key={session.id} className="p-4 hover:bg-muted/50">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={cn(
                                                        "w-10 h-10 rounded-full flex items-center justify-center",
                                                        session.difference === 0 ? "bg-green-100" : 
                                                        session.difference > 0 ? "bg-blue-100" : "bg-red-100"
                                                    )}>
                                                        <CheckCircle className={cn(
                                                            "w-5 h-5",
                                                            session.difference === 0 ? "text-green-600" : 
                                                            session.difference > 0 ? "text-blue-600" : "text-red-600"
                                                        )} />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{session.register_name}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {session.assigned_user_name} • {formatDate(session.date)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold">{formatCurrency(session.total_sales)}</p>
                                                    {session.difference !== null && (
                                                        <p className={cn(
                                                            "text-sm",
                                                            session.difference >= 0 ? "text-green-600" : "text-red-600"
                                                        )}>
                                                            Écart: {session.difference >= 0 ? '+' : ''}{formatCurrency(session.difference)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {session.transfers && session.transfers.length > 0 && (
                                                <div className="mt-2 pl-13 text-sm text-muted-foreground">
                                                    <ArrowRightLeft className="w-3 h-3 inline mr-1" />
                                                    {session.transfers.length} transfert(s)
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {sessionHistory.length === 0 && (
                                        <div className="p-8 text-center text-muted-foreground">
                                            Aucun historique disponible
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Open Register Dialog */}
            <Dialog open={openDialog.open} onOpenChange={(open) => setOpenDialog({ open })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Play className="w-5 h-5 text-green-600" />
                            Ouvrir une caisse
                        </DialogTitle>
                        <DialogDescription>
                            Sélectionnez une caisse et un utilisateur connecté
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Step 1: Select Register */}
                        <div>
                            <label className="text-sm font-medium mb-2 block">1. Sélectionner la caisse</label>
                            <Select value={selectedRegister} onValueChange={setSelectedRegister}>
                                <SelectTrigger data-testid="select-register">
                                    <SelectValue placeholder="Choisir une caisse..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableRegisters.filter(r => r.is_available).map(register => (
                                        <SelectItem key={register.id} value={register.id}>
                                            <div className="flex items-center gap-2">
                                                <span>{register.icon || '💰'}</span>
                                                <span>{register.name}</span>
                                                <span className="text-muted-foreground text-xs">({register.sector})</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Step 2: Select User */}
                        <div>
                            <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                                <UserCheck className="w-4 h-4 text-green-600" />
                                2. Utilisateur connecté
                            </label>
                            <Select value={selectedUser} onValueChange={setSelectedUser}>
                                <SelectTrigger data-testid="select-user">
                                    <SelectValue placeholder="Choisir un utilisateur..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {connectedUsers.map(u => (
                                        <SelectItem 
                                            key={u.id} 
                                            value={u.id}
                                            disabled={u.has_open_register}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className={cn(
                                                    "w-2 h-2 rounded-full",
                                                    u.has_open_register ? "bg-red-500" : "bg-green-500"
                                                )} />
                                                <span>{u.name}</span>
                                                <Badge variant="outline" className="text-xs">{u.role}</Badge>
                                                {u.has_open_register && (
                                                    <span className="text-xs text-red-500">({u.register_name})</span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {connectedUsers.length === 0 && (
                                <p className="text-xs text-amber-600 mt-1">
                                    <AlertCircle className="w-3 h-3 inline mr-1" />
                                    Aucun utilisateur connecté disponible
                                </p>
                            )}
                        </div>
                        
                        {/* Step 3: Opening Cash */}
                        <div>
                            <label className="text-sm font-medium mb-2 block">3. Fond de caisse (F CFA)</label>
                            <Input
                                type="number"
                                placeholder="Ex: 50000"
                                value={openingCash}
                                onChange={(e) => setOpeningCash(e.target.value)}
                                className="text-lg h-12"
                                data-testid="opening-cash-input"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenDialog({ open: false })}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleOpenRegister} 
                            disabled={processing || !selectedRegister || !selectedUser}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="confirm-open-register"
                        >
                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                            Ouvrir la caisse
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Transfer Dialog */}
            <Dialog open={transferDialog.open} onOpenChange={(open) => !open && setTransferDialog({ open: false, session: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                            Transférer la caisse
                        </DialogTitle>
                        <DialogDescription>
                            {transferDialog.session?.register_name} - Actuellement: {transferDialog.session?.assigned_user_name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">Nouvel utilisateur</label>
                            <Select value={transferUser} onValueChange={setTransferUser}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Choisir un utilisateur..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {connectedUsers
                                        .filter(u => u.id !== transferDialog.session?.assigned_user_id && !u.has_open_register)
                                        .map(u => (
                                            <SelectItem key={u.id} value={u.id}>
                                                <div className="flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full bg-green-500" />
                                                    <span>{u.name}</span>
                                                    <Badge variant="outline" className="text-xs">{u.role}</Badge>
                                                </div>
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div>
                            <label className="text-sm font-medium mb-2 block">Notes (optionnel)</label>
                            <Textarea
                                value={transferNotes}
                                onChange={(e) => setTransferNotes(e.target.value)}
                                placeholder="Raison du transfert..."
                                rows={2}
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTransferDialog({ open: false, session: null })}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleTransfer} 
                            disabled={processing || !transferUser}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
                            Transférer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Close Register Dialog */}
            <Dialog open={closeDialog.open} onOpenChange={(open) => !open && setCloseDialog({ open: false, session: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Calculator className="w-5 h-5 text-red-600" />
                            Fermer la caisse
                        </DialogTitle>
                        <DialogDescription>
                            {closeDialog.session?.register_name} - {closeDialog.session?.assigned_user_name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Expected Cash */}
                        <div className="p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-600 mb-1">Espèces attendues</p>
                            <p className="text-2xl font-bold text-blue-700">
                                {formatCurrency(closeDialog.session?.expected_cash)}
                            </p>
                            <div className="flex justify-between text-xs text-blue-600 mt-2">
                                <span>Fond: {formatCurrency(closeDialog.session?.opening_cash)}</span>
                                <span>Ventes espèces: {formatCurrency((closeDialog.session?.expected_cash || 0) - (closeDialog.session?.opening_cash || 0))}</span>
                            </div>
                        </div>
                        
                        {/* Counted Cash */}
                        <div>
                            <label className="text-sm font-medium mb-2 block">Montant compté (F CFA)</label>
                            <Input
                                type="number"
                                placeholder="Entrez le montant compté"
                                value={countedCash}
                                onChange={(e) => setCountedCash(e.target.value)}
                                className="text-lg h-12"
                                data-testid="counted-cash-input"
                            />
                        </div>
                        
                        {/* Difference Preview */}
                        {countedCash && (
                            <div className={cn(
                                "p-4 rounded-lg text-center",
                                parseFloat(countedCash) === closeDialog.session?.expected_cash ? "bg-green-50" :
                                parseFloat(countedCash) > closeDialog.session?.expected_cash ? "bg-blue-50" : "bg-red-50"
                            )}>
                                <p className="text-sm">Écart</p>
                                <p className={cn(
                                    "text-2xl font-bold",
                                    parseFloat(countedCash) === closeDialog.session?.expected_cash ? "text-green-700" :
                                    parseFloat(countedCash) > closeDialog.session?.expected_cash ? "text-blue-700" : "text-red-700"
                                )}>
                                    {parseFloat(countedCash) >= closeDialog.session?.expected_cash ? '+' : ''}
                                    {formatCurrency(parseFloat(countedCash) - (closeDialog.session?.expected_cash || 0))}
                                </p>
                                {Math.abs(parseFloat(countedCash) - (closeDialog.session?.expected_cash || 0)) > (settings.discrepancy_threshold || 2000) && (
                                    <p className="text-xs text-amber-600 mt-2">
                                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                                        Écart important - validation direction requise
                                    </p>
                                )}
                            </div>
                        )}
                        
                        <div>
                            <label className="text-sm font-medium mb-2 block">Notes (optionnel)</label>
                            <Textarea
                                value={closingNotes}
                                onChange={(e) => setClosingNotes(e.target.value)}
                                placeholder="Observations sur la clôture..."
                                rows={2}
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCloseDialog({ open: false, session: null })}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleClose} 
                            disabled={processing || !countedCash}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-close-register"
                        >
                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                            Fermer la caisse
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Unlock Dialog */}
            <Dialog open={unlockDialog.open} onOpenChange={(open) => !open && setUnlockDialog({ open: false, session: null })}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Lock className="w-5 h-5 text-amber-600" />
                            Caisse verrouillée
                        </DialogTitle>
                        <DialogDescription>
                            {unlockDialog.session?.register_name} 
                            {isAdmin ? ' - Accès administrateur' : ' - Entrez votre PIN pour déverrouiller'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {!isAdmin && (
                        <div className="py-4">
                            <Input
                                type="password"
                                placeholder="PIN"
                                value={unlockPin}
                                onChange={(e) => setUnlockPin(e.target.value)}
                                className="text-center text-2xl h-14 tracking-widest"
                                maxLength={6}
                                data-testid="unlock-pin-input"
                            />
                        </div>
                    )}
                    
                    {isAdmin && (
                        <div className="py-4">
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                                <p className="text-green-700 font-medium">Accès administrateur</p>
                                <p className="text-sm text-green-600">Vous pouvez déverrouiller sans PIN</p>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUnlockDialog({ open: false, session: null })}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleUnlock} 
                            disabled={processing || (!isAdmin && unlockPin.length < 4)}
                            className="bg-amber-600 hover:bg-amber-700"
                        >
                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlock className="w-4 h-4 mr-2" />}
                            Déverrouiller
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Validation Dialog */}
            <Dialog open={validationDialog.open} onOpenChange={(open) => !open && setValidationDialog({ open: false, session: null })}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-600" />
                            Valider la clôture avec écart
                        </DialogTitle>
                    </DialogHeader>
                    
                    {validationDialog.session && (
                        <div className="py-4 space-y-4">
                            <div className="p-4 bg-muted rounded-lg space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Caisse</span>
                                    <span className="font-medium">{validationDialog.session.register_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Caissier</span>
                                    <span className="font-medium">{validationDialog.session.assigned_user_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Date</span>
                                    <span className="font-medium">{formatDate(validationDialog.session.date)}</span>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-blue-50 rounded-lg text-center">
                                    <p className="text-sm text-blue-600">Attendu</p>
                                    <p className="text-xl font-bold text-blue-700">
                                        {formatCurrency(validationDialog.session.expected_cash)}
                                    </p>
                                </div>
                                <div className="p-3 bg-green-50 rounded-lg text-center">
                                    <p className="text-sm text-green-600">Compté</p>
                                    <p className="text-xl font-bold text-green-700">
                                        {formatCurrency(validationDialog.session.counted_cash)}
                                    </p>
                                </div>
                            </div>
                            
                            <div className={cn(
                                "p-4 rounded-lg text-center",
                                validationDialog.session.difference >= 0 ? "bg-blue-100" : "bg-red-100"
                            )}>
                                <p className="text-sm">Écart</p>
                                <p className={cn(
                                    "text-2xl font-bold",
                                    validationDialog.session.difference >= 0 ? "text-blue-700" : "text-red-700"
                                )}>
                                    {validationDialog.session.difference >= 0 ? '+' : ''}
                                    {formatCurrency(validationDialog.session.difference)}
                                </p>
                            </div>
                            
                            <div>
                                <label className="text-sm font-medium mb-2 block">Notes du manager (optionnel)</label>
                                <Textarea
                                    value={validationNotes}
                                    onChange={(e) => setValidationNotes(e.target.value)}
                                    placeholder="Commentaires sur la clôture..."
                                    rows={2}
                                />
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            variant="destructive" 
                            onClick={() => handleValidation(false)}
                            disabled={processing}
                            className="w-full sm:w-auto"
                        >
                            <XCircle className="w-4 h-4 mr-2" />
                            Rejeter (recompter)
                        </Button>
                        <Button 
                            onClick={() => handleValidation(true)}
                            disabled={processing}
                            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                        >
                            {processing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Valider
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={historyDialog.open} onOpenChange={(open) => !open && setHistoryDialog({ open: false, session: null })}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="w-5 h-5 text-purple-600" />
                            Historique de la session
                        </DialogTitle>
                        <DialogDescription>
                            {historyDialog.session?.register_name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <ScrollArea className="h-[300px]">
                        <div className="space-y-3 py-4">
                            {sessionEvents.map((event) => (
                                <div key={event.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                                    <div className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                                        event.event_type === 'OPENED' && "bg-green-100",
                                        event.event_type === 'TRANSFERRED' && "bg-blue-100",
                                        event.event_type === 'LOCKED' && "bg-amber-100",
                                        event.event_type === 'AUTO_LOCKED' && "bg-amber-100",
                                        event.event_type === 'UNLOCKED' && "bg-green-100",
                                        event.event_type === 'CLOSED' && "bg-red-100"
                                    )}>
                                        {event.event_type === 'OPENED' && <Play className="w-4 h-4 text-green-600" />}
                                        {event.event_type === 'TRANSFERRED' && <ArrowRightLeft className="w-4 h-4 text-blue-600" />}
                                        {(event.event_type === 'LOCKED' || event.event_type === 'AUTO_LOCKED') && <Lock className="w-4 h-4 text-amber-600" />}
                                        {event.event_type === 'UNLOCKED' && <Unlock className="w-4 h-4 text-green-600" />}
                                        {event.event_type === 'CLOSED' && <X className="w-4 h-4 text-red-600" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium text-sm">{event.event_type.replace('_', ' ')}</p>
                                            <p className="text-xs text-muted-foreground">{formatTime(event.created_at)}</p>
                                        </div>
                                        <p className="text-sm text-muted-foreground">{event.user_name}</p>
                                        {event.details && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {Object.entries(event.details).map(([key, value]) => (
                                                    <span key={key} className="mr-2">
                                                        {key}: {typeof value === 'number' ? formatCurrency(value) : String(value)}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {sessionEvents.length === 0 && (
                                <p className="text-center text-muted-foreground py-8">
                                    Aucun événement enregistré
                                </p>
                            )}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            {/* Settings Dialog */}
            <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Paramètres des caisses
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Seuil d'écart pour validation (F CFA)
                            </label>
                            <Input
                                type="number"
                                value={settings.discrepancy_threshold || ''}
                                onChange={(e) => setSettings({...settings, discrepancy_threshold: parseInt(e.target.value)})}
                                placeholder="2000"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Au-delà de ce montant, la validation direction est requise
                            </p>
                        </div>
                        
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Délai d'inactivité pour verrouillage (minutes)
                            </label>
                            <Input
                                type="number"
                                value={settings.inactivity_timeout_minutes || ''}
                                onChange={(e) => setSettings({...settings, inactivity_timeout_minutes: parseInt(e.target.value)})}
                                placeholder="5"
                            />
                        </div>
                        
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Seuil utilisateur connecté (minutes)
                            </label>
                            <Input
                                type="number"
                                value={settings.connected_threshold_minutes || ''}
                                onChange={(e) => setSettings({...settings, connected_threshold_minutes: parseInt(e.target.value)})}
                                placeholder="15"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Un utilisateur est considéré connecté s'il a été actif dans ce délai
                            </p>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSettingsDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSaveSettings} disabled={processing}>
                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Emergency Close Dialog - Admin Only */}
            <Dialog 
                open={emergencyDialog.open} 
                onOpenChange={(open) => {
                    if (!open) {
                        setEmergencyDialog({ open: false, session: null });
                        setEmergencyPassword('');
                        setEmergencyNotes('');
                    }
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertOctagon className="w-5 h-5" />
                            Fermeture d'urgence
                        </DialogTitle>
                        <DialogDescription>
                            Cette action va immédiatement fermer la caisse et déconnecter l'utilisateur assigné.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Warning Box */}
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                                <ShieldAlert className="w-4 h-4" />
                                Action irréversible
                            </div>
                            <ul className="text-xs text-red-600 space-y-1 list-disc pl-4">
                                <li>La session sera fermée immédiatement</li>
                                <li>L'utilisateur <strong>{emergencyDialog.session?.assigned_user_name}</strong> sera déconnecté</li>
                                <li>Tous les paniers en cours seront supprimés</li>
                                <li>L'historique sera conservé pour traçabilité</li>
                            </ul>
                        </div>
                        
                        {/* Register Info */}
                        <div className="p-3 bg-muted rounded-lg text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Caisse</span>
                                <span className="font-medium">{emergencyDialog.session?.register_name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Utilisateur</span>
                                <span className="font-medium">{emergencyDialog.session?.assigned_user_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Ventes</span>
                                <span className="font-medium">{formatCurrency(emergencyDialog.session?.total_sales)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Espèces attendues</span>
                                <span className="font-bold">{formatCurrency(emergencyDialog.session?.expected_cash)}</span>
                            </div>
                        </div>
                        
                        {/* Password Confirmation */}
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Votre code PIN administrateur (6 chiffres) *
                            </label>
                            <Input
                                type="password"
                                value={emergencyPassword}
                                onChange={(e) => setEmergencyPassword(e.target.value)}
                                placeholder="Entrez votre code PIN"
                                maxLength={6}
                                data-testid="emergency-password"
                            />
                        </div>
                        
                        {/* Optional Notes */}
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Motif de la fermeture d'urgence
                            </label>
                            <textarea
                                className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                                value={emergencyNotes}
                                onChange={(e) => setEmergencyNotes(e.target.value)}
                                placeholder="Raison de la fermeture forcée (optionnel)"
                                data-testid="emergency-notes"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setEmergencyDialog({ open: false, session: null });
                                setEmergencyPassword('');
                                setEmergencyNotes('');
                            }}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handleEmergencyClose}
                            disabled={processing || !emergencyPassword}
                            data-testid="confirm-emergency-close"
                        >
                            {processing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <AlertOctagon className="w-4 h-4 mr-2" />
                            )}
                            Confirmer la fermeture
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Cash Closing Modal - Detailed Counting */}
            <CashClosingModal
                open={cashClosingModal.open}
                onClose={() => setCashClosingModal({ open: false, registerId: null, registerName: '', sessionId: null })}
                registerId={cashClosingModal.registerId}
                registerName={cashClosingModal.registerName}
                sessionId={cashClosingModal.sessionId}
                api={api}
                onClosingComplete={() => {
                    fetchData();
                    toast.success('Clôture soumise - En attente de validation par un directeur');
                }}
            />
        </div>
    );
};

export default RegisterManagementPage;
